import type { FeatureName } from '@/config/features.config';
import { ROUTES } from '@/config/routes.config';

type NavLinkItem = {
  url: string;
  label: string;
  disabled?: boolean;
  feature?: FeatureName;
  /** Rutas que marcan este ítem como activo (para items que agrupan varias vistas, ej. Diseño + Tarifas) */
  activePaths?: string[];
};

/**
 * Sub-items del Sidebar principal (Admin).
 * Solo data: el Sidebar agrega `badge`, `matches`, y `onClick`.
 */
export const SIDEBAR_SUBNAV = {
  home: [
    { url: ROUTES.HOME, label: 'Resumen' },
    { url: ROUTES.CONTROL_CENTER, label: 'Dashboard' },
    { url: ROUTES.CONTROL_CENTER_2, label: 'Dashboard 2' },
  ] satisfies NavLinkItem[],
  orders: [
    { url: ROUTES.ORDERS_ONLINE, label: 'Pedidos online' },
    { url: ROUTES.ORDERS_ABANDONED, label: 'Carritos abandonados' },
  ] satisfies NavLinkItem[],
  dropshipping: [
    { url: ROUTES.DROPSHIPPING_DROPI, label: 'Dropi' },
    { url: ROUTES.DROPSHIPPING_ALICLIK, label: 'Aliclik' },
  ] satisfies NavLinkItem[],
  products: [
    { url: ROUTES.PRODUCTS_CATEGORIES, label: 'Categorías' },
    { url: ROUTES.PRODUCTS_TAGS, label: 'Etiquetas' },
    { url: ROUTES.PRODUCTS_MODIFIERS, label: 'Modificadores' },
    { url: ROUTES.PRODUCTS_VARIANTS, label: 'Variantes' },
    { url: ROUTES.PRODUCTS_OFFERS, label: 'Ofertas' },
    { url: ROUTES.PRODUCTS_INVENTORY, label: 'Inventario' },
    { url: ROUTES.PRODUCTS_PHOTO_STUDIO, label: 'Estudio de foto' },
    { url: ROUTES.PRODUCTS_MARKETPLACE_READINESS, label: 'Preparación marketplaces' },
  ] satisfies NavLinkItem[],
  reports: [
    { url: ROUTES.REPORTES, label: 'Reportes' },
    { url: ROUTES.SALES_HEATMAP, label: 'Mapa de calor' },
    { url: ROUTES.SALES_RANKING, label: 'Ranking de productos' },
    { url: ROUTES.SIMULADOR_RENTABILIDAD, label: 'Simulador de rentabilidad' },
    { url: ROUTES.FINANZAS_PNL, label: 'Finanzas (P&L)' },
    { url: ROUTES.COMISIONES, label: 'Comisiones de asesores' },
  ] satisfies NavLinkItem[],
  onlineStore: [
    { url: ROUTES.ONLINE_STORE_THEMES, label: 'Temas' },
    { url: ROUTES.ONLINE_STORE_PAGES, label: 'Páginas' },
    { url: ROUTES.ONLINE_STORE_NAVIGATION, label: 'Menús (Navegación)' },
    { url: ROUTES.ONLINE_STORE_PREFERENCES, label: 'Preferencias' },
  ] satisfies NavLinkItem[],
  pos: [
    { url: ROUTES.APPS_RESTAURANT_POS, label: 'POS', feature: 'pos' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_TABLES, label: 'Mesas', feature: 'pos' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_KDS, label: 'KDS', feature: 'kds' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_MENU, label: 'Menú', feature: 'pos' as FeatureName },
  ] satisfies NavLinkItem[],
  loyalty: [
    { url: ROUTES.APPS_LOYALTY_DASHBOARD, label: 'Resumen' },
    { url: ROUTES.APPS_LOYALTY_CUSTOMERS, label: 'Clientes' },
    { url: ROUTES.APPS_LOYALTY_REWARDS, label: 'Recompensas' },
    { url: ROUTES.APPS_LOYALTY_HISTORY, label: 'Historial' },
    { url: ROUTES.APPS_LOYALTY_SETTINGS, label: 'Configuración' },
    { url: ROUTES.APPS_LOYALTY_QR_SCANNER, label: 'Escáner QR', feature: 'loyalty' as FeatureName },
  ] satisfies NavLinkItem[],
  contabilidad: [
    { url: ROUTES.APPS_CONTABILIDAD, label: 'Resumen' },
    { url: ROUTES.APPS_CONTABILIDAD_COMPROBANTES, label: 'Comprobantes' },
    { url: ROUTES.APPS_CONTABILIDAD_CONFIG, label: 'Configuración' },
  ] satisfies NavLinkItem[],
  /** Formulario COD: solo configuración del formulario de compra (diseño, integraciones, etc.) */
  cod: [
    { url: ROUTES.APPS_COD_DESIGN, label: 'Diseño del Formulario', activePaths: [ROUTES.APPS_COD_DESIGN, ROUTES.APPS_COD_SHIPPING] },
    { url: ROUTES.APPS_COD_FRAUD, label: 'Prevención de Fraude' },
    { url: ROUTES.APPS_COD_DELIVERY_SUCCESS, label: 'Éxito de Entrega' },
    { url: ROUTES.APPS_COD_SALES_BOOSTER, label: 'Impulsor de Ventas' },
    { url: ROUTES.APPS_COD_ANALYTICS, label: 'Analítica' },
    { url: ROUTES.APPS_COD_PAYMENTS, label: 'Adelanto' },
    { url: ROUTES.APPS_COD_SETTINGS, label: 'Configuraciones e integraciones' },
  ] satisfies NavLinkItem[],
  /** WhatsApp: inbox y configuración */
  whatsapp: [
    { url: ROUTES.WHATSAPP_INBOX, label: 'Inbox' },
    // La pantalla existía desde hacía meses y NO estaba en el menú: solo se llegaba escribiendo la
    // URL. Una funcionalidad a la que hay que saber llegar de memoria es una que no existe.
    { url: ROUTES.WHATSAPP_CAMPAIGNS, label: 'Campañas' },
    { url: ROUTES.WHATSAPP_SUPERVISOR, label: 'Supervisor' },
    { url: ROUTES.WHATSAPP_SLA, label: 'SLA y alertas' },
    { url: ROUTES.WHATSAPP_STATS, label: 'Métricas' },
    { url: ROUTES.WHATSAPP_TEAMS, label: 'Equipos' },
    { url: ROUTES.WHATSAPP_ASSIGNMENT_RULES, label: 'Reglas de asignación' },
    { url: ROUTES.WHATSAPP_SKILLS, label: 'Skills y enrutamiento' },
    { url: ROUTES.WHATSAPP_MACROS, label: 'Macros' },
    { url: ROUTES.WHATSAPP_AUTOMATED_MESSAGES, label: 'Mensajes automáticos' },
    { url: ROUTES.WHATSAPP_WEBHOOKS, label: 'Webhooks' },
    // 'Configuración' (WHATSAPP_SETTINGS) removido — la config ahora vive
    // por canal en /chats/inboxes/{id}/{section}. La ruta legacy redirige.
  ] satisfies NavLinkItem[],
  /** CRM: contactos, deals, comunicaciones (las opciones de voz/centralita viven en `callCenter`). */
  crm: [
    { url: ROUTES.APPS_CRM_INBOX, label: 'Inbox' },
    { url: ROUTES.APPS_CRM_LEADS, label: 'Contactos' },
    { url: ROUTES.APPS_CRM_PIPELINE, label: 'Pipeline' },
    { url: ROUTES.APPS_CRM_TASKS, label: 'Tareas' },
    { url: ROUTES.APPS_CRM_SCHEDULE, label: 'Agenda' },
    { url: ROUTES.APPS_CRM_SEQUENCES, label: 'Secuencias' },
    { url: ROUTES.APPS_CRM_DASHBOARD, label: 'Dashboard' },
    { url: ROUTES.APPS_CRM_AUTOMATIONS, label: 'Automatizaciones' },
    { url: ROUTES.APPS_CRM_EMAIL, label: 'Email' },
    { url: ROUTES.APPS_CRM_EMAIL_TEMPLATES, label: 'Plantillas de email' },
    { url: ROUTES.APPS_CRM_RECOMPRA, label: 'Recompra' },
    { url: ROUTES.APPS_CRM_COPILOT, label: 'Copiloto', feature: 'copilot' as FeatureName },
    { url: ROUTES.APPS_CRM_CONVERSION, label: 'Reporte de conversión' },
    { url: ROUTES.APPS_CRM_MERGE, label: 'Fusionar contactos' },
  ] satisfies NavLinkItem[],
  /** Llamadas: módulo principal de voz (Voice Commerce). Item L1 dedicado en sidebar. */
  callCenter: [
    { url: ROUTES.APPS_CRM_DIALER, label: 'Marcador' },
    { url: ROUTES.APPS_CRM_CALLS, label: 'Historial' },
    { url: ROUTES.APPS_CALL_CENTER_CART_RECOVERY, label: 'Cart Recovery' },
    { url: ROUTES.APPS_CALL_CENTER_CAMPAIGNS, label: 'Campañas' },
    { url: ROUTES.APPS_CALL_CENTER_SUPERVISOR, label: 'Supervisión' },
    { url: ROUTES.APPS_CALL_CENTER_STATS, label: 'Estadísticas' },
    { url: ROUTES.APPS_CALL_CENTER_EXTENSIONS, label: 'Extensiones' },
    { url: ROUTES.APPS_CRM_VOICE_AI, label: 'Voice AI' },
    { url: ROUTES.APPS_CRM_CALL_COACHING, label: 'Coaching de llamadas' },
    { url: ROUTES.APPS_CALL_CENTER, label: 'Configuración' },
  ] satisfies NavLinkItem[],
  /** Operaciones: pedidos, ventas, logística y cobranzas. (Transporte es su propio ítem.) */
  operaciones: [
    // Dominio único (orders_v2). Las páginas COD legacy (Ventas/Logística/Cobranzas) quedaron
    // consolidadas aquí — leen el mismo orders_v2 vía las vistas.
    { url: ROUTES.OPS_PEDIDOS, label: 'Presupuestos' },
    { url: ROUTES.OPS_VENTAS, label: 'Ventas' },
    { url: ROUTES.OPS_LOGISTICA, label: 'Entregas' },
    { url: ROUTES.OPS_COBRANZAS, label: 'Cobranzas' },
    { url: ROUTES.OPS_CANCELADAS, label: 'Canceladas' },
    { url: ROUTES.OPS_ENTREGAS_RECAUDO, label: 'Entregas y Recaudo' },
    // Al final: configuración del módulo (couriers, roles, defaults). Solo admin (filtrado en Sidebar).
    { url: ROUTES.OPS_SETTINGS, label: 'Configuraciones' },
  ] satisfies NavLinkItem[],
  /** Transporte: agencias (Shalom, etc.) y motorizados — kept for backward compat */
  transporte: [
    { url: ROUTES.TRANSPORT_AGENCIES, label: 'Agencias' },
    { url: ROUTES.TRANSPORT_MOTORIZADOS, label: 'Motorizados' },
  ] satisfies NavLinkItem[],
  /** Chatbot Builder: flujos, AI y widget embebible */
  chatbot: [
    { url: ROUTES.APPS_CHATBOT, label: 'Mis chatbots' },
  ] satisfies NavLinkItem[],
  /** Agent Platform (Smarty Agent Studio — F2/Fase B).
   *  Los Chatbots viven aquí como "Flujos": son agentes deterministas sin LLM,
   *  caso particular de la misma plataforma (ver [[project_smarty_agent_platform_vision]]).
   */
  agents: [
    { url: ROUTES.AGENTS_STUDIO, label: 'Mis agentes' },
    // Segundo a propósito: es lo primero que un dueño quiere ver de sus agentes —qué saben y qué
    // aprendieron—, y además es donde esperan las preguntas mineradas que necesitan su OK.
    { url: ROUTES.AGENTS_CEREBRO, label: 'Cerebro' },
    { url: ROUTES.AGENTS_KNOWLEDGE_BASE, label: 'Base de conocimiento' },
    { url: ROUTES.AGENTS_GHOSTWRITER, label: 'Crear con IA' },
    { url: ROUTES.APPS_CHATBOT, label: 'Flujos (chatbots)' },
    { url: ROUTES.AGENTS_CATALOG, label: 'Catálogo' },
    { url: ROUTES.AGENTS_OUTCOMES, label: 'Outcomes' },
  ] satisfies NavLinkItem[],
  settings: [
    { url: ROUTES.SETTINGS_GENERAL, label: 'General' },
    { url: ROUTES.SETTINGS_BUSINESS, label: 'Datos de la empresa' },
    { url: ROUTES.SETTINGS_PLAN, label: 'Plan y facturación' },
    { url: ROUTES.SETTINGS_USERS, label: 'Usuarios y permisos' },
    { url: ROUTES.SETTINGS_PAYMENTS, label: 'Pagos', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_CHECKOUT, label: 'Checkout', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_SHIPPING, label: 'Envíos y entrega', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_DOMAINS, label: 'Dominios', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_BRAND, label: 'Marca', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_NOTIFICATIONS, label: 'Notificaciones', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_APPS_AND_SALES_CHANNELS, label: 'Apps y canales' },
    { url: ROUTES.SETTINGS_INTEGRATIONS, label: 'Integraciones' },
  ] satisfies NavLinkItem[],
} as const;

/**
 * Sub-ítems del Settings sidebar (fuente de verdad para los desplegables de
 * Usuarios y Envíos que consume SettingsNav.tsx). El resto del menú de Settings
 * vive en el array GROUPS de SettingsNav.tsx.
 */
export const SETTINGS_NAV = {
  usersSubItems: [
    { url: ROUTES.SETTINGS_USERS_ROLES, label: 'Roles' },
    { url: ROUTES.SETTINGS_USERS_SECURITY, label: 'Seguridad' },
  ] satisfies NavLinkItem[],
  shippingSubItems: [
    { url: ROUTES.SETTINGS_DELIVERY_METHODS, label: 'Métodos de entrega' },
    { url: ROUTES.SETTINGS_DELIVERY_RULES, label: 'Reglas de delivery' },
    { url: ROUTES.SETTINGS_LOGISTICS_COURIER_RULES, label: 'Couriers por zona' },
    { url: ROUTES.SETTINGS_SHIPPING_METHODS, label: 'Métodos de envío' },
  ] satisfies NavLinkItem[],
} as const;

export const NAV_BADGES = {
  soon: 'Próx.',
  beta: 'Beta',
} as const;

type BadgeKey = keyof typeof NAV_BADGES;

type SidebarItemKey =
  | 'home'
  | 'orders'
  | 'products'
  | 'customers'
  | 'content'
  | 'analytics'
  | 'marketing'
  | 'discounts'
  | 'onlineStore'
  | 'pos'
  | 'marketplaces'
  | 'whatsapp'
  | 'loyalty'
  | 'cod'
  | 'crm'
  | 'callCenter'
  | 'operaciones'
  | 'transporte'
  | 'dropshipping'
  | 'chatbot'
  | 'ads'
  | 'agentsStudio'
  | 'contabilidad'
  | 'modules'
  | 'settings';

// Note: Not all SidebarItemKey values are used in the current SIDEBAR_MAIN.
// Some items (callCenter, transporte, chatbot, content, marketing, discounts, modules)
// were consolidated into parent sections or moved to sub-navigation for a cleaner sidebar.
// The keys remain in the union type for backward compatibility with Sidebar.tsx.

type SidebarSectionKey = 'core' | 'salesChannels' | 'apps' | 'footer';

export type SidebarNavItem = {
  key: SidebarItemKey;
  url: string;
  label: string;
  subnavKey?: keyof typeof SIDEBAR_SUBNAV;
  badge?: BadgeKey;
  feature?: FeatureName;
};

/**
 * Sidebar — restauración de descubribilidad total.
 *
 * Filosofía: cada módulo es un canal/app independiente con su propia config.
 * Visibilidad máxima de opciones; el usuario decide su orden.
 * (Versión previa había aplicado regla de Miller 7±2 ocultando 9 items L1
 *  y 17 subitems. Restaurado por solicitud del founder.)
 */
export const SIDEBAR_MAIN = {
  sections: [
    {
      key: 'core' as const,
      title: undefined,
      items: [
        { key: 'home', url: ROUTES.HOME, label: 'Inicio', subnavKey: 'home' },
        { key: 'orders', url: ROUTES.ORDERS_ONLINE, label: 'Pedidos', subnavKey: 'orders' },
        { key: 'products', url: ROUTES.PRODUCTS, label: 'Productos', subnavKey: 'products' },
        { key: 'customers', url: ROUTES.CUSTOMERS, label: 'Clientes' },
        { key: 'crm', url: ROUTES.APPS_CRM_INBOX, label: 'CRM', subnavKey: 'crm', feature: 'crm' as FeatureName },
        { key: 'content', url: ROUTES.CONTENT, label: 'Contenido' },
        { key: 'analytics', url: ROUTES.ANALYTICS, label: 'Analíticas', subnavKey: 'reports' },
        { key: 'marketing', url: ROUTES.MARKETING, label: 'Marketing', feature: 'marketing' as FeatureName },
        { key: 'discounts', url: ROUTES.DISCOUNTS, label: 'Descuentos', feature: 'discounts' as FeatureName },
        { key: 'operaciones', url: ROUTES.OPS_VENTAS, label: 'Operaciones', subnavKey: 'operaciones', feature: 'operaciones' as FeatureName },
        { key: 'dropshipping', url: ROUTES.DROPSHIPPING, label: 'Dropshipping', subnavKey: 'dropshipping', feature: 'logistics' as FeatureName },
        { key: 'transporte', url: ROUTES.TRANSPORT_AGENCIES, label: 'Transporte', subnavKey: 'transporte', feature: 'logistics' as FeatureName },
      ] satisfies SidebarNavItem[],
    },
    {
      key: 'salesChannels' as const,
      title: 'Canales',
      items: [
        { key: 'whatsapp', url: ROUTES.WHATSAPP_INBOX, label: 'Chats', subnavKey: 'whatsapp', feature: 'whatsapp' as FeatureName },
        { key: 'callCenter', url: ROUTES.APPS_CRM_DIALER, label: 'Llamadas', subnavKey: 'callCenter', feature: 'crm' as FeatureName },
        { key: 'onlineStore', url: ROUTES.ONLINE_STORE, label: 'Tienda online', subnavKey: 'onlineStore', feature: 'ecommerce' as FeatureName },
        { key: 'pos', url: ROUTES.APPS_RESTAURANT_POS, label: 'Punto de venta', subnavKey: 'pos', feature: 'pos' as FeatureName },
        { key: 'marketplaces', url: ROUTES.MARKETPLACES, label: 'Marketplaces' },
      ] satisfies SidebarNavItem[],
    },
    {
      key: 'apps' as const,
      title: 'Apps',
      items: [
        // Agentes IA bajó de "Canales" a "Apps": no es un canal de venta, es una app
        // transversal que opera SOBRE los canales (Chats, Llamadas, etc.).
        { key: 'agentsStudio', url: ROUTES.AGENTS_STUDIO, label: 'Agentes IA', subnavKey: 'agents', feature: 'agents' as FeatureName },
        { key: 'ads', url: ROUTES.ADS_CHAT, label: 'Smarty Ads', badge: 'beta', feature: 'ads' as FeatureName },
        { key: 'contabilidad', url: ROUTES.APPS_CONTABILIDAD, label: 'Contabilidad', subnavKey: 'contabilidad', badge: 'beta', feature: 'accounting' as FeatureName },
        { key: 'loyalty', url: ROUTES.APPS_LOYALTY, label: 'Fidelización', subnavKey: 'loyalty', feature: 'loyalty' as FeatureName },
        { key: 'cod', url: ROUTES.APPS_COD, label: 'Formulario COD', subnavKey: 'cod', feature: 'cod' as FeatureName },
      ] satisfies SidebarNavItem[],
    },
    {
      key: 'footer' as const,
      title: undefined,
      items: [
        { key: 'settings', url: ROUTES.SETTINGS, label: 'Configuración', subnavKey: 'settings' },
      ] satisfies SidebarNavItem[],
    },
  ] satisfies Array<{ key: SidebarSectionKey; title?: string; items: SidebarNavItem[] }>,
} as const;

